import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, BookOpen, Package, Home, ShoppingCart, Settings as SettingsIcon, Calendar, Users } from 'lucide-react';

const PAGES = [
  { name: 'Home',          path: '/',              icon: Home },
  { name: 'Recipes',       path: '/recipes',       icon: BookOpen },
  { name: 'Inventory',     path: '/inventory',     icon: Package },
  { name: 'Shopping List', path: '/shopping-list', icon: ShoppingCart },
  { name: 'Meal Planner',  path: '/meal-planner',  icon: Calendar },
  { name: 'Community',     path: '/community',     icon: Users },
  { name: 'Settings',      path: '/settings',      icon: SettingsIcon },
];

const fuzzyMatch = (haystack, needle) =>
  haystack && haystack.toLowerCase().includes(needle.toLowerCase());

const GlobalSearch = ({ open, onClose, recipes = [], inventory = [] }) => {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const results = useMemo(() => {
    const q = query.trim();
    if (!q) {
      return [{ group: 'Pages', items: PAGES.map((p) => ({ ...p, type: 'page' })) }];
    }
    const recipeMatches = recipes
      .filter((r) => fuzzyMatch(r.name, q))
      .slice(0, 6)
      .map((r, i) => ({ type: 'recipe', name: r.name, path: '/recipes', icon: BookOpen, _i: i }));
    const inventoryMatches = inventory
      .filter((i) => fuzzyMatch(i.name, q))
      .slice(0, 6)
      .map((i, idx) => ({ type: 'inventory', name: i.name, path: '/inventory', icon: Package, _i: idx }));
    const pageMatches = PAGES
      .filter((p) => fuzzyMatch(p.name, q))
      .map((p) => ({ ...p, type: 'page' }));
    const groups = [];
    if (recipeMatches.length)   groups.push({ group: 'Recipes',   items: recipeMatches });
    if (inventoryMatches.length) groups.push({ group: 'Inventory', items: inventoryMatches });
    if (pageMatches.length)      groups.push({ group: 'Pages',     items: pageMatches });
    return groups;
  }, [query, recipes, inventory]);

  const flatResults = useMemo(
    () => results.flatMap((g) => g.items),
    [results]
  );

  useEffect(() => {
    if (activeIndex >= flatResults.length) setActiveIndex(0);
  }, [flatResults.length, activeIndex]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = flatResults[activeIndex];
      if (target?.path) {
        navigate(target.path);
        onClose();
      }
    }
  };

  if (!open) return null;

  let runningIndex = -1;

  return (
    <div
      className="cmdk-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label="Global search"
    >
      <div className="cmdk-panel">
        <div className="cmdk-input-row">
          <Search size={18} aria-hidden="true" className="cmdk-input-icon" />
          <input
            ref={inputRef}
            type="text"
            className="cmdk-input"
            placeholder="Search recipes, inventory, pages…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
            onKeyDown={onKeyDown}
            aria-label="Search"
          />
          <kbd className="cmdk-kbd">esc</kbd>
        </div>

        <div className="cmdk-results" role="listbox">
          {results.length === 0 && (
            <p className="cmdk-empty">No results.</p>
          )}
          {results.map((group) => (
            <div key={group.group} className="cmdk-group">
              <p className="cmdk-group-label">{group.group}</p>
              {group.items.map((item) => {
                runningIndex += 1;
                const Icon = item.icon || Search;
                const isActive = runningIndex === activeIndex;
                return (
                  <button
                    key={`${item.type}-${item.name}`}
                    type="button"
                    className={`cmdk-item${isActive ? ' is-active' : ''}`}
                    onMouseEnter={() => setActiveIndex(runningIndex)}
                    onClick={() => { navigate(item.path); onClose(); }}
                    role="option"
                    aria-selected={isActive}
                  >
                    <Icon size={16} aria-hidden="true" className="cmdk-item-icon" />
                    <span className="cmdk-item-name">{item.name}</span>
                    <span className="cmdk-item-type">{item.type}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
